import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./ResidenceForm.css";

/**
 * Fallback local pour appeler l'API avec Bearer token si aucun util "fetchWithAuth" n'est dispo.
 */
async function fetchAuth(input, init = {}) {
  const token =
    localStorage.getItem("token") ||
    sessionStorage.getItem("token") ||
    "";
  const headers = {
    "Content-Type": "application/json",
    ...(init.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const res = await fetch(input, { ...init, headers });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const message = data?.error || `Erreur ${res.status}`;
    throw new Error(message);
  }
  return res.json().catch(() => ({}));
}

export default function ResidenceForm() {
  const { id } = useParams();               // si présent => édition
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    address: "",
    postalCode: "",
    city: "",
    apartmentsCount: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);

  // Chargement des données en édition
  useEffect(() => {
    let stop = false;
    async function load() {
      if (!isEdit) return;
      try {
        setBusy(true);
        setError("");
        // tente d'utiliser un util interne s'il existe déjà
        const useFetchWithAuth = (() => {
          try { return require("../utils/api")?.fetchWithAuth; } catch { return null; }
        })();
        const loader = useFetchWithAuth || fetchAuth;
        const data = await loader(`/api/residences/${id}`);
        const r = data?.residence || data;
        if (stop) return;
        setForm({
          name: r?.name || "",
          address: r?.address || "",
          postalCode: r?.postalCode || "",
          city: r?.city || "",
          apartmentsCount: (r?.apartmentsCount ?? "").toString(),
        });
      } catch (e) {
        setError(e.message || "Impossible de charger la résidence.");
      } finally {
        if (!stop) setBusy(false);
      }
    }
    load();
    return () => { stop = true; };
  }, [id, isEdit]);

  const canSubmit = useMemo(() => {
    return (
      form.name.trim() &&
      form.address.trim() &&
      /^\d{4,6}$/.test(form.postalCode.trim()) &&
      form.city.trim() &&
      String(form.apartmentsCount).trim() !== "" &&
      Number(form.apartmentsCount) >= 0
    );
  }, [form]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    setError("");
    setOk(false);
    try {
      const method = isEdit ? "PUT" : "POST";
      const url = isEdit ? `/api/residences/${id}` : "/api/residences";
      await fetchAuth(url, {
        method,
        body: JSON.stringify({
          name: form.name.trim(),
          address: form.address.trim(),
          postalCode: form.postalCode.trim(),
          city: form.city.trim(),
          apartmentsCount: Number(form.apartmentsCount),
        }),
      });
      setOk(true);
      setTimeout(() => navigate("/residences"), 600);
    } catch (e) {
      setError(e.message || "Une erreur est survenue.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="res-create-page">
      <div className="res-card">
        <header className="res-header">
          <h1>{isEdit ? "Modifier la résidence" : "Ajouter une résidence"}</h1>
          <p className="res-sub">
            {isEdit
              ? "Mettez à jour les informations de la résidence."
              : "Renseignez les informations de base. Vous pourrez compléter les détails après la création."}
          </p>
        </header>

        <form onSubmit={handleSubmit} noValidate className="res-form">
          <div className="res-field">
            <label htmlFor="name">Nom *</label>
            <input
              id="name"
              type="text"
              placeholder="Ex. Résidence Les Flamboyants"
              value={form.name}
              onChange={update("name")}
              required
            />
          </div>

          <div className="res-field">
            <label htmlFor="address">Adresse *</label>
            <input
              id="address"
              type="text"
              placeholder="12 Chemin des Tamarins"
              value={form.address}
              onChange={update("address")}
              required
            />
          </div>

          <div className="res-grid">
            <div className="res-field">
              <label htmlFor="postalCode">Code postal *</label>
              <input
                id="postalCode"
                type="text"
                inputMode="numeric"
                placeholder="97400"
                value={form.postalCode}
                onChange={update("postalCode")}
                required
              />
            </div>

            <div className="res-field">
              <label htmlFor="city">Ville *</label>
              <input
                id="city"
                type="text"
                placeholder="Saint-Denis"
                value={form.city}
                onChange={update("city")}
                required
              />
            </div>
          </div>

          <div className="res-field">
            <label htmlFor="apartmentsCount">Nombre d'appartements</label>
            <input
              id="apartmentsCount"
              type="number"
              min="0"
              step="1"
              placeholder="Ex. 24"
              value={form.apartmentsCount}
              onChange={update("apartmentsCount")}
            />
          </div>

          {error ? <div className="res-alert error">{error}</div> : null}
          {ok ? (
            <div className="res-alert success">
              {isEdit ? "Résidence mise à jour." : "Résidence créée."} Redirection…
            </div>
          ) : null}

          <div className="res-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate("/residences")}
              disabled={busy}
            >
              Annuler
            </button>
            <button className="btn-primary" type="submit" disabled={!canSubmit || busy}>
              {busy ? (isEdit ? "Enregistrement…" : "Création…") : (isEdit ? "Enregistrer" : "Créer")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
